<template>
    <ui-modal
        ref="confirmationModal"
        class="pending-account-information-modal"
        centered
        :title="$t('Update information')"
        @hidden="resetModal"
    >
        <div>
            <p v-if="!update">{{ $t('Enter the information requested below') }}:</p>
            <div>
                <ui-input
                    v-if="!user.first_name"
                    id="first_name"
                    v-model="formData.first_name"
                    name="first_name"
                    type="text"
                    placeholder="John"
                    :label="$t('First Name')"
                    :field="v$.formData.first_name"
                />
                <ui-input
                    v-if="!user.last_name"
                    id="last_name"
                    v-model="formData.last_name"
                    name="last_name"
                    type="text"
                    placeholder="Doe"
                    :label="$t('Last Name')"
                    :field="v$.formData.last_name"
                />
                <ui-select
                    v-if="!user.country"
                    id="country"
                    v-model="formData.country"
                    name="country"
                    option-label="name"
                    option-value="code"
                    :label="$t('Country')"
                    :placeholder="$t('Choose an option')"
                    :options="countryList"
                    :field="v$.formData.country"
                />
                <ui-input
                    v-if="update || !user.email"
                    id="email"
                    v-model="formData.email"
                    name="email"
                    type="email"
                    placeholder="test@example.com"
                    :label="getLabel('Email')"
                    :field="v$.formData.email"
                >
                    <template #prepend>
                        <i class="fas fa-envelope"></i>
                    </template>
                </ui-input>
                <ui-input
                    v-if="!user.phone  || update"
                    id="phone"
                    v-model="formData.phone"
                    name="phone"
                    type="number"
                    placeholder="55 1254 5678"
                    :label="getLabel('Phone')"
                    :field="v$.formData.phone"
                >
                    <template v-if="phoneCode" #prepend>
                        +{{ phoneCode }}
                    </template>
                </ui-input>
            </div>
            <vue-recaptcha
                class="w-100"
                ref="recaptcha"
                size="invisible"
                @verify="updateAccount"
                :sitekey="CONST.CAPTCHACODE"
                :loadRecaptchaScript="true"
            />
        </div>
        <template #footer>
            <ui-button variant="secondary" :disabled="updatingAccount" @click="$refs.confirmationModal.close()">
                {{ $t('Cancel') }}
            </ui-button>
            <ui-button :loading="updatingAccount" @click="handleSubmit">
                {{ $t('Save') }}
            </ui-button>
        </template>
    </ui-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { helpers, maxLength, minLength, required } from '@vuelidate/validators';
import { getCountryCallingCode } from 'libphonenumber-js';
import VueRecaptcha from 'vue-recaptcha';

import UiButton from '@/components/ui/buttons/Button.vue';
import UiInput from '@/components/ui/inputs/Input.vue';
import UiSelect from '@/components/ui/inputs/Select.vue';
import UiModal from '@/components/ui/modal/Modal.vue';

import recaptchaUtils from '@/utils/recaptcha.utils';

export default {
    name: 'PendingAccountInformationModal',
    components: {
        UiSelect,
        UiButton,
        UiInput,
        UiModal,
        VueRecaptcha,
    },
    props: {
        user: {
            type: Object,
            default: null,
            required: true,
        },
        update: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        const form_data = {};

        if (!this.user.phone) {
            form_data.phone = '';
        }

        if (!this.user.country) {
            form_data.country = '';
        }

        if (!this.user.first_name) {
            form_data.first_name = '';
        }

        if (!this.user.last_name) {
            form_data.last_name = '';
        }

        return {
            countryList: [],
            formData: { ...form_data },
            initialFormData: { ...form_data },
            updatingAccount: false,
        };
    },
    computed: {
        phoneCode() {
            const country = this.user?.country || this.formData?.country;

            if (country) {
                return getCountryCallingCode(country);
            }

            return null
        },
        rules() {
            const rules = { formData: {} };

            if (!this.user.country) {
                rules.formData.country = {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('country') }), required),
                    $autoDirty: true,
                };
            }

            if (!this.user.phone) {
                rules.formData.phone = {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('phone') }), required),
                    minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(8)),
                    maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(12)),
                    $autoDirty: true,
                };
            }

            if (!this.user.first_name) {
                rules.formData.first_name = {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('first name') }), required),
                    minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(2)),
                    maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(50)),
                    $autoDirty: true,
                };
            }
            if (!this.user.last_name) {
                rules.formData.last_name = {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('last name') }), required),
                    minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(2)),
                    maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(50)),
                    $autoDirty: true,
                };
            }

            return rules;
        },
    },
    validations() {
        return this.rules;
    },
    async mounted() {
        if (!this.user.country) {
            this.countryList = await this.fnApiGetCountries();
        }
    },
    methods: {
        close() {
            this.$refs.confirmationModal.close();
        },
        show() {
            this.$refs.confirmationModal.show();
        },
        resetModal() {
            this.formData = this.initialFormData;
        },
        async handleSubmit() {
            this.v$.$touch();

            if (this.v$.$invalid) {
                return;
            }

            if (!recaptchaUtils.isAvailable()) {
                this.updateAccount('');
                return;
            }

            this.$refs.recaptcha.reset();
            this.$refs.recaptcha.execute();
        },
        async updateAccount(token) {
            try {
                this.updatingAccount = true;

                this.updatingAccount = true;
                let payload = { token };

                if (this.formData.phone) {
                    payload.phone = this.formData.phone;
                    payload.phone_code = this.phoneCode;
                    payload.phone_country = this.formData.country || this.user.country;
                }

                if (this.formData.email) {
                    payload.email = this.formData.email;
                }

                if (this.formData.country) {
                    payload.country = this.formData.country;
                    payload.phone_code = this.phoneCode;
                    payload.phone_country = this.formData.country;

                }

                if (this.formData.first_name) {
                    payload.first_name = this.formData.first_name;
                }
                if (this.formData.last_name) {
                    payload.last_name = this.formData.last_name;
                }

                const { data } = await this.axiosAccount.put(`/api/accounts/me`, payload);

                this.$emit('accountInformationUpdated', data);
                this.resetModal();
                this.close();
            } catch(error) {
                this.showError(error);
            } finally {
                this.updatingAccount = false;
            }
        },
        getLabel(input) {
            if (this.update) {
                let inputs = {
                    'Email': this.$t('Email (optional)'),
                    'Phone': this.$t('Phone (optional)'),
                };
                return inputs[input];
            } else {
                return this.$t(input);
            }
        }
    },
};
</script>

<style lang="scss">
.pending-account-information-modal {
    &__link {
        color: #004C74 !important;
        font-weight: 600 !important;
    }
}
</style>
